import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { ReviewChange, useDeleteTourReview, useUpdateTourReview } from '~/hooks/apis/reviews/useTourReviews';

import { Review } from '~/services/ReviewService';

interface Props {
  tourId: string;
  review: Review;
}

type Editing = 'none' | 'wording' | 'response';

/** One review: read it, hide or show it, reply, fix its wording, or delete it. */
export default function NativeTourReviewCard({ tourId, review }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const update = useUpdateTourReview(tourId);
  const remove = useDeleteTourReview(tourId);
  const [editing, setEditing] = useState<Editing>('none');
  const [title, setTitle] = useState(review.title ?? '');
  const [text, setText] = useState(review.review);
  const [response, setResponse] = useState(review.response?.message ?? '');
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  // External reviews aren't LE's to change.
  const isOurs = review.source !== 'google';
  const isBusy = update.isPending || remove.isPending;

  const save = async (change: ReviewChange, done: string) => {
    try {
      await update.mutateAsync({ reviewId: review.id, change });
      enqueueSnackbar(done, { variant: 'success' });
      setEditing('none');
    } catch (error) {
      enqueueSnackbar(`Couldn't update the review: ${error.message}`, { variant: 'error' });
    }
  };

  const startEditing = (next: Editing) => {
    setTitle(review.title ?? '');
    setText(review.review);
    setResponse(review.response?.message ?? '');
    setEditing(next);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, opacity: review.isPubliclyVisible ? 1 : 0.75 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Box>
            <Rating value={review.rating} readOnly size="small" />
            <Typography variant="body2" color="text.secondary">
              {review.userName}
              {review.userCountry ? ` · ${review.userCountry}` : ''} ·{' '}
              {dayjs(review.submittedDate ?? review.createdAt).format('D MMM YYYY')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {review.source && <Chip size="small" variant="outlined" label={review.source} />}
            {!review.isPubliclyVisible && <Chip size="small" color="warning" label="Hidden" />}
          </Stack>
        </Stack>

        {editing === 'wording' ? (
          <Stack spacing={2}>
            <TextField size="small" label="Title" value={title} onChange={(event) => setTitle(event.target.value)} />
            <TextField
              label="Review"
              value={text}
              onChange={(event) => setText(event.target.value)}
              error={!text.trim()}
              helperText={!text.trim() ? 'Required' : undefined}
              multiline
              minRows={3}
              required
            />
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                disabled={isBusy || !text.trim() || (title === (review.title ?? '') && text === review.review)}
                onClick={() =>
                  save(
                    {
                      ...(title !== (review.title ?? '') ? { title } : {}),
                      ...(text !== review.review ? { review: text } : {}),
                    },
                    'Review wording saved',
                  )
                }
              >
                Save wording
              </Button>
              <Button variant="text" onClick={() => setEditing('none')}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Box>
            {review.title && <Typography fontWeight="bold">{review.title}</Typography>}
            <Typography sx={{ whiteSpace: 'pre-line' }}>{review.review}</Typography>
          </Box>
        )}

        {editing === 'response' ? (
          <Stack spacing={2}>
            <TextField
              label="Our reply"
              value={response}
              onChange={(event) => setResponse(event.target.value)}
              helperText="Shown under the review on the tour page"
              multiline
              minRows={2}
            />
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                disabled={isBusy || !response.trim() || response === (review.response?.message ?? '')}
                onClick={() => save({ response }, 'Reply saved')}
              >
                Save reply
              </Button>
              <Button variant="text" onClick={() => setEditing('none')}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          review.response?.message && (
            <Box sx={{ borderLeft: 3, borderColor: 'divider', pl: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Our reply
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {review.response.message}
              </Typography>
            </Box>
          )
        )}

        {isOurs && editing === 'none' && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              size="small"
              variant="text"
              disabled={isBusy}
              onClick={() =>
                save(
                  { isPubliclyVisible: !review.isPubliclyVisible },
                  review.isPubliclyVisible ? 'Review hidden' : 'Review shown',
                )
              }
            >
              {review.isPubliclyVisible ? 'Hide' : 'Show'}
            </Button>
            <Button size="small" variant="text" disabled={isBusy} onClick={() => startEditing('response')}>
              {review.response?.message ? 'Edit reply' : 'Reply'}
            </Button>
            <Button size="small" variant="text" disabled={isBusy} onClick={() => startEditing('wording')}>
              Edit wording
            </Button>
            <Button
              size="small"
              variant="text"
              color="error"
              disabled={isBusy}
              onClick={() => setIsConfirmingDelete(true)}
            >
              Delete
            </Button>
          </Stack>
        )}
      </Stack>

      <Dialog open={isConfirmingDelete} onClose={() => setIsConfirmingDelete(false)}>
        <DialogTitle>Delete this review?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            It's removed for good, from this tour and every tour sharing its reviews. To take it off the tour page for
            now, hide it instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setIsConfirmingDelete(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={remove.isPending}
            onClick={async () => {
              try {
                await remove.mutateAsync(review.id);
                enqueueSnackbar('Review deleted', { variant: 'success' });
                setIsConfirmingDelete(false);
              } catch (error) {
                enqueueSnackbar(`Couldn't delete the review: ${error.message}`, { variant: 'error' });
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
